// Phase 9 — Accounting Records hub
// Master data + chart of accounts + journal entries + general ledger
// landing page for accountants.
import { useEffect, useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Users, ShoppingBag, UserCog, BookOpen, BookMarked, Wallet,
  Plus, Loader2, ArrowRight, BookCopy, ListTree, Pencil, Trash2,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const FM_PLUS = ["system_admin", "salihi_finance_manager"];
const fmt = (v) => `$${Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const TYPE_BADGE = {
  asset: "bg-sky-100 text-sky-700",
  liability: "bg-rose-100 text-rose-700",
  equity: "bg-violet-100 text-violet-700",
  income: "bg-emerald-100 text-emerald-700",
  expense: "bg-amber-100 text-amber-700",
};

export default function AccountingHubPage() {
  const { user, can } = useAuth();
  const canManageCoA = FM_PLUS.includes(user?.role) || user?.full_access === true;
  // Phase 22 (2026-07-10) — granular CoA edit/delete keys.
  const canEditCoA = can ? can("accounting.coa.edit") : canManageCoA;
  const canDeleteCoA = can ? can("accounting.coa.delete") : canManageCoA;
  const [systemCodes, setSystemCodes] = useState(new Set());
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [recentJEs, setRecentJEs] = useState([]);
  const [coaFilter, setCoaFilter] = useState("all");
  const [newAcct, setNewAcct] = useState(false);
  const [selectedJE, setSelectedJE] = useState(null);

  // General ledger quick-access state
  const [glAccount, setGlAccount] = useState("");
  const [glStart, setGlStart] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [glEnd, setGlEnd] = useState(() => new Date().toISOString().slice(0, 10));
  const [ledger, setLedger] = useState(null);
  const [glLoading, setGlLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, s, c, e, j] = await Promise.all([
        api.get("/accounts"),
        api.get("/suppliers"),
        api.get("/customers"),
        api.get("/employees"),
        api.get("/journal-entries?page=1&page_size=10"),
      ]);
      setAccounts(a.data || []);
      setSuppliers(s.data || []);
      setCustomers(c.data || []);
      setEmployees(e.data || []);
      setRecentJEs(j.data?.items || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Phase 22 — fetch SYSTEM_ACCOUNT_CODES once.
  useEffect(() => {
    api.get("/accounts/system-codes")
      .then(({ data }) => setSystemCodes(new Set(data.system_account_codes || [])))
      .catch(() => {});
  }, []);

  const counts = useMemo(() => ({
    accounts: accounts.filter((a) => a.is_active !== false).length,
    suppliers: suppliers.filter((p) => p.is_active !== false).length,
    customers: customers.filter((p) => p.is_active !== false).length,
    employees: employees.filter((p) => p.is_active !== false).length,
  }), [accounts, suppliers, customers, employees]);

  const filteredCoA = useMemo(() => {
    const base = coaFilter === "all"
      ? accounts
      : accounts.filter((a) => a.type === coaFilter);
    // Phase 52-E (2026-08-06) — sort children directly under their parent
    // (still numeric by code within each family). Top-level accounts are
    // interleaved in the natural code order; each top-level row is
    // followed by its children (also numeric by code).
    const byCode = new Map(base.map((a) => [a.code, a]));
    const topLevel = base.filter((a) => !a.parent_code || !byCode.has(a.parent_code));
    topLevel.sort((a, b) => String(a.code).localeCompare(String(b.code)));
    const children = base.filter((a) => a.parent_code && byCode.has(a.parent_code));
    const childrenByParent = new Map();
    for (const c of children) {
      const arr = childrenByParent.get(c.parent_code) || [];
      arr.push(c); childrenByParent.set(c.parent_code, arr);
    }
    for (const arr of childrenByParent.values()) {
      arr.sort((a, b) => String(a.code).localeCompare(String(b.code)));
    }
    const out = [];
    for (const t of topLevel) {
      out.push({ ...t, _hasChildren: (childrenByParent.get(t.code) || []).length > 0 });
      for (const c of (childrenByParent.get(t.code) || [])) {
        out.push({ ...c, _parentName: t.name });
      }
    }
    return out;
  }, [accounts, coaFilter]);

  async function loadLedger() {
    if (!glAccount) { toast.error("Pick an account first"); return; }
    setGlLoading(true);
    try {
      const { data } = await api.get(`/accounts/${glAccount}/ledger`, {
        params: { date_from: glStart, date_to: glEnd },
      });
      setLedger(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setGlLoading(false); }
  }

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-[#4FA3D1]" /></div>;

  return (
    <div className="space-y-6" data-testid="accounting-hub-page">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-slate-900">Accounting Records</h2>
        <p className="text-sm text-slate-500 mt-1">
          Master data, chart of accounts, journal entries, and general ledger. The foundation of your books.
        </p>
      </div>

      {/* Metric strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="hub-metric-strip">
        <Metric label="Active Suppliers" value={counts.suppliers} icon={ShoppingBag}
                accent="text-sky-700 bg-sky-50" to="/suppliers" testId="metric-suppliers" />
        <Metric label="Active Customers" value={counts.customers} icon={Users}
                accent="text-emerald-700 bg-emerald-50" to="/customers" testId="metric-customers" />
        <Metric label="Active Employees" value={counts.employees} icon={UserCog}
                accent="text-violet-700 bg-violet-50" to="/employees" testId="metric-employees" />
        <Metric label="Chart of Accounts" value={counts.accounts} icon={ListTree}
                accent="text-amber-700 bg-amber-50" testId="metric-coa"
                sub={`${counts.accounts} accounts active`}
                onClick={() => document.getElementById("coa-section")?.scrollIntoView({ behavior: "smooth" })} />
      </div>

      {/* Quick links grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="hub-quick-links">
        <QuickLink to="/suppliers"   icon={ShoppingBag} title="Suppliers"             desc="Vendor master with consultants & WHT tagging" />
        <QuickLink to="/customers"   icon={Users}       title="Customers"             desc="Customer master & receivables sources" />
        <QuickLink to="/employees"   icon={UserCog}     title="Employees"             desc="Headcount & base salary for payroll" />
        <QuickLink onScroll          icon={ListTree}    title="Chart of Accounts"     desc="All ledger accounts grouped by type" target="coa-section" />
        <QuickLink to="/bookkeeping" icon={BookOpen}    title="Bookkeeping"           desc="Journal entries with full posting trail" />
        <QuickLink onScroll          icon={BookMarked}  title="General Ledger"        desc="Drill into any account's running balance" target="gl-section" />
      </div>

      {/* Chart of Accounts inline */}
      <Card id="coa-section" data-testid="hub-coa-section">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-end justify-between flex-wrap gap-3">
            <div>
              <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <ListTree className="h-5 w-5 text-[#4FA3D1]" /> Chart of Accounts
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">{counts.accounts} active accounts · grouped by type</p>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs text-slate-500">Filter type</Label>
              <Select value={coaFilter} onValueChange={setCoaFilter}>
                <SelectTrigger className="w-36" data-testid="coa-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  <SelectItem value="asset">Asset</SelectItem>
                  <SelectItem value="liability">Liability</SelectItem>
                  <SelectItem value="equity">Equity</SelectItem>
                  <SelectItem value="income">Income</SelectItem>
                  <SelectItem value="expense">Expense</SelectItem>
                </SelectContent>
              </Select>
              {canManageCoA && (
                <Button size="sm" onClick={() => setNewAcct(true)} className="bg-[#4FA3D1] hover:bg-[#4392bd] text-white" data-testid="coa-new-btn">
                  <Plus className="h-4 w-4 mr-1.5" /> New Account
                </Button>
              )}
            </div>
          </div>
          <div className="overflow-x-auto rounded-md border border-slate-200">
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <TableHead>Code</TableHead><TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                {/* Phase 52-E (2026-08-06) — Parent column for hierarchy */}
                <TableHead>Parent</TableHead>
                <TableHead>Active</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {filteredCoA.length === 0
                  ? <TableRow><TableCell colSpan={6} className="text-center py-6 text-slate-400">No accounts.</TableCell></TableRow>
                  : filteredCoA.map((a) => (
                    <TableRow key={a.id} data-testid={`coa-row-${a.code}`}
                              className={a.parent_code ? "bg-slate-50/50" : ""}>
                      <TableCell className="font-mono">
                        {a.parent_code ? (
                          <span className="inline-flex items-center gap-1 pl-4 text-slate-500">
                            <ChevronRight className="h-3 w-3" />{a.code}
                          </span>
                        ) : (
                          <span className={a._hasChildren ? "font-semibold text-slate-900" : ""}>
                            {a.code}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className={a.parent_code ? "pl-6 text-slate-700" : "font-medium"}>{a.name}</TableCell>
                      <TableCell><Badge className={`border-0 ${TYPE_BADGE[a.type] || ""}`}>{a.type}</Badge></TableCell>
                      <TableCell className="text-xs text-slate-500">
                        {a.parent_code
                          ? <span className="font-mono">{a.parent_code}</span>
                          : (a._hasChildren
                              ? <span className="text-slate-400 italic">Top-level</span>
                              : "—")}
                      </TableCell>
                      <TableCell>{a.is_active === false
                        ? <Badge variant="secondary">inactive</Badge>
                        : <Badge className="bg-emerald-100 text-emerald-700 border-0">active</Badge>}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end items-center gap-1">
                          <Button size="sm" variant="outline" onClick={() => { setGlAccount(a.id); document.getElementById("gl-section")?.scrollIntoView({ behavior: "smooth" }); }} data-testid={`coa-ledger-${a.code}`}>
                            View ledger →
                          </Button>
                          {canEditCoA && (
                            <Button
                              size="sm" variant="ghost"
                              className="h-8 w-8 p-0"
                              title="Edit account"
                              onClick={() => setEditing(a)}
                              data-testid={`coa-edit-${a.code}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          )}
                          {canDeleteCoA && (
                            <Button
                              size="sm" variant="ghost"
                              className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                              title={systemCodes.has(a.code)
                                ? "System account — cannot be deleted"
                                : "Delete account"}
                              disabled={systemCodes.has(a.code)}
                              onClick={() => setDeleting(a)}
                              data-testid={`coa-delete-${a.code}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Recent Journal Entries */}
      <Card data-testid="hub-recent-jes">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-[#4FA3D1]" /> Recent Journal Entries
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">Latest 10 posted entries — the source of truth for your books</p>
            </div>
            <Link to="/bookkeeping" className="text-sm text-[#4FA3D1] hover:underline inline-flex items-center gap-1" data-testid="hub-view-all-jes">
              View all <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          <div className="overflow-x-auto rounded-md border border-slate-200">
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <TableHead>Date</TableHead><TableHead>Ref</TableHead>
                <TableHead>Narration</TableHead><TableHead>Source</TableHead>
                <TableHead className="text-right">Total</TableHead><TableHead>Posted by</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {recentJEs.length === 0
                  ? <TableRow><TableCell colSpan={6} className="text-center py-6 text-slate-400">No journal entries yet.</TableCell></TableRow>
                  : recentJEs.map((je) => (
                    <TableRow key={je.id} className="cursor-pointer hover:bg-slate-50 transition-colors"
                              onClick={() => setSelectedJE(je)} data-testid={`hub-je-row-${je.id}`}>
                      <TableCell>{je.entry_date}</TableCell>
                      <TableCell className="font-mono text-xs">{je.reference || "—"}</TableCell>
                      <TableCell className="text-sm">{je.narration}</TableCell>
                      <TableCell><Badge variant="secondary">{je.source_type}</Badge></TableCell>
                      <TableCell className="text-right tabular-nums font-semibold">{fmt(je.total_debit)}</TableCell>
                      <TableCell className="text-xs text-slate-500">{je.posted_by_email}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* General Ledger Quick Access */}
      <Card id="gl-section" data-testid="hub-gl-section">
        <CardContent className="p-5 space-y-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <BookMarked className="h-5 w-5 text-[#4FA3D1]" /> General Ledger
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">Pick an account and date range to see its running balance.</p>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs">Account</Label>
              <Select value={glAccount} onValueChange={setGlAccount}>
                <SelectTrigger data-testid="gl-account-select"><SelectValue placeholder="Pick an account…" /></SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {accounts.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.code} — {a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">From</Label>
              <Input type="date" value={glStart} onChange={(e) => setGlStart(e.target.value)} data-testid="gl-start" />
            </div>
            <div>
              <Label className="text-xs">To</Label>
              <Input type="date" value={glEnd} onChange={(e) => setGlEnd(e.target.value)} data-testid="gl-end" />
            </div>
            <Button onClick={loadLedger} disabled={glLoading} className="bg-[#4FA3D1] hover:bg-[#4392bd] text-white" data-testid="gl-load-btn">
              {glLoading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <BookCopy className="h-4 w-4 mr-1.5" />}
              View Ledger
            </Button>
          </div>
          {ledger && (
            <div className="space-y-3" data-testid="gl-result">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Pill label="Opening" value={fmt(ledger.opening_balance)} />
                <Pill label="Total Debits" value={fmt(ledger.total_debit)} accent="text-emerald-700" />
                <Pill label="Total Credits" value={fmt(ledger.total_credit)} accent="text-rose-700" />
                <Pill label="Closing" value={fmt(ledger.closing_balance)} accent="font-bold text-slate-900" />
              </div>
              <div className="overflow-x-auto rounded-md border border-slate-200">
                <Table>
                  <TableHeader><TableRow className="bg-slate-50">
                    <TableHead>Date</TableHead><TableHead>Ref</TableHead>
                    <TableHead>Narration</TableHead>
                    <TableHead className="text-right">Debit</TableHead>
                    <TableHead className="text-right">Credit</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {(ledger.entries || []).length === 0
                      ? <TableRow><TableCell colSpan={6} className="text-center py-6 text-slate-400">No activity in range.</TableCell></TableRow>
                      : ledger.entries.map((r, i) => (
                        <TableRow key={i}>
                          <TableCell>{r.entry_date}</TableCell>
                          <TableCell className="font-mono text-xs">{r.reference || "—"}</TableCell>
                          <TableCell className="text-sm">{r.narration}</TableCell>
                          <TableCell className="text-right tabular-nums">{fmt(r.debit)}</TableCell>
                          <TableCell className="text-right tabular-nums">{fmt(r.credit)}</TableCell>
                          <TableCell className="text-right tabular-nums font-semibold">{fmt(r.balance)}</TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* New Account dialog */}
      {newAcct && <NewAccountDialog onClose={() => setNewAcct(false)} onCreated={() => { setNewAcct(false); load(); }} />}

      {/* Phase 22 — Edit + Delete dialogs */}
      {editing && <EditAccountDialog acct={editing} isSystem={systemCodes.has(editing.code)} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {deleting && <DeleteAccountDialog acct={deleting} onClose={() => setDeleting(null)} onDeleted={(summary) => { setDeleting(null); load(); toast.success(summary); }} />}

      {/* JE detail dialog */}
      {selectedJE && <JEDetailDialog je={selectedJE} onClose={() => setSelectedJE(null)} />}
    </div>
  );
}

function EditAccountDialog({ acct, isSystem, onClose, onSaved }) {
  const [f, setF] = useState({
    code: acct.code, name: acct.name || "",
    description: acct.description || "", is_active: !!acct.is_active,
    // Phase 52-E (2026-08-06) — allow reassigning the parent from Edit.
    parent_code: acct.parent_code || "",
  });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  // Load candidate parents (same type, top-level, active, not self).
  const [parentOptions, setParentOptions] = useState([]);
  useEffect(() => {
    api.get("/accounts").then((r) => {
      const rows = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      setParentOptions(rows
        .filter((a) => a.type === acct.type
                       && a.is_active !== false
                       && !a.parent_code
                       && a.code !== acct.code)
        .sort((a, b) => String(a.code).localeCompare(String(b.code))));
    }).catch(() => setParentOptions([]));
  }, [acct.type, acct.code]);
  const save = async () => {
    try {
      // Send `""` when the user picked "None" — the backend clears the parent.
      await api.patch(`/accounts/${acct.id}`, f);
      toast.success(`Account ${acct.code} updated.`);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="edit-account-dialog">
        <DialogHeader><DialogTitle>Edit account {acct.code}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label>Code {isSystem && <span className="text-xs text-slate-500">(system — locked)</span>}</Label>
            <Input value={f.code} onChange={(e) => set("code", e.target.value)}
                   disabled={isSystem} data-testid="edit-acct-code"
                   title={isSystem ? "System account code is fixed." : ""} />
          </div>
          <div><Label>Name</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="edit-acct-name" /></div>
          <div><Label>Description</Label><Input value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="edit-acct-desc" /></div>
          <div>
            <Label>Parent (optional)</Label>
            <Select
              value={f.parent_code || "__none__"}
              onValueChange={(v) => set("parent_code", v === "__none__" ? "" : v)}
            >
              <SelectTrigger data-testid="edit-acct-parent"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-[280px]">
                <SelectItem value="__none__" data-testid="edit-acct-parent-none">
                  — None (top-level account)
                </SelectItem>
                {parentOptions.map((p) => (
                  <SelectItem key={p.id} value={p.code}
                              data-testid={`edit-acct-parent-opt-${p.code}`}>
                    {p.code} — {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-slate-500 mt-1">
              Same-type top-level accounts only. Clearing sets this back to a top-level account.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input id="ac-active" type="checkbox" checked={f.is_active} onChange={(e) => set("is_active", e.target.checked)} data-testid="edit-acct-active" />
            <Label htmlFor="ac-active">Active</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} className="bg-[#4FA3D1] hover:bg-[#4392bd] text-white" data-testid="edit-acct-save">Save changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteAccountDialog({ acct, onClose, onDeleted }) {
  const [confirmText, setConfirmText] = useState("");
  const submit = async () => {
    try {
      const { data } = await api.delete(`/accounts/${acct.id}`,
        { data: { confirm_code: confirmText } });
      const ren = (data.renumbered || []).length;
      const refs = Object.values(data.cascade_counts || {}).reduce((a, b) => a + b, 0);
      onDeleted(`Account ${acct.code} deleted. ${ren} account(s) renumbered. ${refs} reference(s) updated.`);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="delete-account-dialog">
        <DialogHeader><DialogTitle>Delete account {acct.code}?</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-slate-700">
            <span className="font-mono">{acct.code}</span> · <span className="font-medium">{acct.name}</span> · <Badge variant="outline">{acct.type}</Badge>
          </p>
          <p className="text-amber-700 text-xs border-l-2 border-amber-400 bg-amber-50 px-3 py-2 rounded">
            This will renumber every subsequent account in the same range and propagate the new codes to all journal entries, bank links, and transaction posting fields. <b>This cannot be undone.</b>
          </p>
          <div>
            <Label>Type <span className="font-mono">{acct.code}</span> to confirm</Label>
            <Input value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder={acct.code}
                    data-testid="delete-acct-confirm" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="delete-acct-cancel">Cancel</Button>
          <Button
            className="bg-red-600 hover:bg-red-700 text-white"
            disabled={confirmText !== acct.code}
            onClick={submit}
            data-testid="delete-acct-submit"
          >
            Delete account
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Metric({ label, value, icon: Icon, accent, to, onClick, sub, testId }) {
  const inner = (
    <Card className="hover:shadow-md transition-shadow cursor-pointer" data-testid={testId}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
            <p className="text-2xl font-bold text-slate-900 mt-1 tabular-nums">{value}</p>
            {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
          </div>
          <div className={`p-2.5 rounded-md ${accent}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
  if (to) return <Link to={to}>{inner}</Link>;
  return <div onClick={onClick} role="button">{inner}</div>;
}

function QuickLink({ to, target, icon: Icon, title, desc, onScroll }) {
  const body = (
    <Card className="hover:shadow-md hover:border-[#4FA3D1] transition-all cursor-pointer h-full" data-testid={`quick-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <CardContent className="p-4 space-y-2">
        <div className="flex items-center gap-2">
          <Icon className="h-5 w-5 text-[#4FA3D1]" />
          <p className="font-semibold text-slate-900">{title}</p>
        </div>
        <p className="text-xs text-slate-500 leading-relaxed">{desc}</p>
      </CardContent>
    </Card>
  );
  if (to) return <Link to={to}>{body}</Link>;
  if (onScroll) return <div role="button" onClick={() => document.getElementById(target)?.scrollIntoView({ behavior: "smooth" })}>{body}</div>;
  return body;
}

function Pill({ label, value, accent = "text-slate-900" }) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
      <p className="text-[10px] uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`text-base tabular-nums ${accent}`}>{value}</p>
    </div>
  );
}

function NewAccountDialog({ onClose, onCreated }) {
  const [f, setF] = useState({ code: "", name: "", type: "asset", is_active: true, parent_code: "" });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  // Phase 52-E (2026-08-06) — dynamic Parent dropdown filtered by Type
  // (only same-type top-level accounts are eligible).
  const [allAccounts, setAllAccounts] = useState([]);
  useEffect(() => {
    api.get("/accounts").then((r) => {
      const rows = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      setAllAccounts(rows);
    }).catch(() => setAllAccounts([]));
  }, []);
  const parentOptions = useMemo(() => (
    allAccounts
      .filter((a) => a.type === f.type
                     && a.is_active !== false
                     && !a.parent_code)   // top-level only
      .sort((a, b) => String(a.code).localeCompare(String(b.code)))
  ), [allAccounts, f.type]);

  // Reset parent when the type changes (any picked parent would be invalid).
  useEffect(() => { setF((s) => ({ ...s, parent_code: "" })); }, [f.type]);

  const submit = async () => {
    try {
      const payload = { ...f };
      if (!payload.parent_code) delete payload.parent_code;
      await api.post("/accounts", payload);
      toast.success(`Account ${f.code} created.`);
      onCreated();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="new-account-dialog">
        <DialogHeader><DialogTitle>New account</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label>Code</Label><Input value={f.code} onChange={(e) => set("code", e.target.value)} placeholder="e.g. 1400.01" data-testid="acct-code" /></div>
          <div><Label>Name</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Prepaid Rent" data-testid="acct-name" /></div>
          <div>
            <Label>Type</Label>
            <Select value={f.type} onValueChange={(v) => set("type", v)}>
              <SelectTrigger data-testid="acct-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="asset">Asset</SelectItem>
                <SelectItem value="liability">Liability</SelectItem>
                <SelectItem value="equity">Equity</SelectItem>
                <SelectItem value="income">Income</SelectItem>
                <SelectItem value="expense">Expense</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Parent (optional)</Label>
            <Select
              value={f.parent_code || "__none__"}
              onValueChange={(v) => set("parent_code", v === "__none__" ? "" : v)}
            >
              <SelectTrigger data-testid="acct-parent"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-[280px]">
                <SelectItem value="__none__" data-testid="acct-parent-none">
                  — None (top-level account)
                </SelectItem>
                {parentOptions.length === 0 ? null : parentOptions.map((p) => (
                  <SelectItem key={p.id} value={p.code}
                              data-testid={`acct-parent-opt-${p.code}`}>
                    {p.code} — {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-slate-500 mt-1">
              Leave empty for a top-level account. When set, this account rolls up under the parent on Balance Sheet / P&amp;L.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} className="bg-[#4FA3D1] hover:bg-[#4392bd] text-white" data-testid="acct-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function JEDetailDialog({ je, onClose }) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="je-detail-dialog">
        <DialogHeader><DialogTitle>{je.reference || "Journal Entry"}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Date</Label><p>{je.entry_date}</p></div>
            <div><Label className="text-xs">Source</Label><p><Badge variant="secondary">{je.source_type}</Badge></p></div>
            <div className="col-span-2"><Label className="text-xs">Narration</Label><p>{je.narration}</p></div>
          </div>
          <div className="overflow-x-auto rounded-md border border-slate-200">
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <TableHead>Code</TableHead><TableHead>Account</TableHead>
                <TableHead className="text-right">Debit</TableHead>
                <TableHead className="text-right">Credit</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {(je.lines || []).map((l, i) => (
                  <TableRow key={i}>
                    <TableCell className="font-mono">{l.account_code}</TableCell>
                    <TableCell>{l.account_name}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmt(l.debit)}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmt(l.credit)}</TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-slate-50 font-semibold">
                  <TableCell colSpan={2}>Total</TableCell>
                  <TableCell className="text-right tabular-nums">{fmt(je.total_debit)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmt(je.total_credit)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
          <p className="text-xs text-slate-500">Posted by {je.posted_by_email} · {(je.posted_at || "").slice(0, 19).replace("T", " ")}</p>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
